import { useState, useEffect, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { useToast } from "@/hooks/use-toast";
import { fetchWithAuth } from '@/lib/fetchWithAuth';
import { Plus, X, Save } from "lucide-react";
import { PerguntaFormulario, RegraNavegacao, BlocoPerguntas } from '@/types/tarefas';

interface PerguntaFormularioEditorProps {
  editingPergunta: PerguntaFormulario | null;
  todasPerguntas: PerguntaFormulario[];
  onSave: (pergunta: PerguntaFormulario, perguntaAnteriorId: string, respostasAnteriores: string[]) => void;
  onCancel: () => void;
  obterVinculoAnterior: (perguntaId: string, perguntas: PerguntaFormulario[]) => { pergunta: PerguntaFormulario; tipo: 'multipla_escolha' | 'discursiva'; respostas: string[] } | null;
  getPerguntaLabel: (perguntaId: string, perguntasBase?: PerguntaFormulario[]) => string;
  isBlocoContext?: boolean; // Indica se está sendo usado no contexto de blocos de perguntas
}

export function PerguntaFormularioEditor({
  editingPergunta,
  todasPerguntas,
  onSave,
  onCancel,
  obterVinculoAnterior,
  getPerguntaLabel,
  isBlocoContext = false
}: PerguntaFormularioEditorProps) {
  const { toast } = useToast();
  const [perguntaFormData, setPerguntaFormData] = useState<Partial<PerguntaFormulario>>({
    texto: '',
    tipo_resposta: 'discursiva',
    opcoes: [],
    ordem: 0,
    regras_navegacao: [],
    finaliza_fluxo: false
  });
  const [temFluxoCondicional, setTemFluxoCondicional] = useState<boolean>(false);
  const [perguntaAnteriorId, setPerguntaAnteriorId] = useState<string>('');
  const [tipoPerguntaAnterior, setTipoPerguntaAnterior] = useState<'multipla_escolha' | 'discursiva' | ''>('');
  const [respostasAnteriores, setRespostasAnteriores] = useState<string[]>([]);
  const [blocosDisponiveis, setBlocosDisponiveis] = useState<BlocoPerguntas[]>([]);
  const [tipoVinculo, setTipoVinculo] = useState<'pergunta' | 'bloco'>('pergunta');
  const [blocoSelecionadoId, setBlocoSelecionadoId] = useState<string>('');

  useEffect(() => {
    // Buscar blocos disponíveis
    const fetchBlocos = async () => {
      try {
        const response = await fetchWithAuth('/api/blocos-perguntas');
        if (response.ok) {
          const data: BlocoPerguntas[] = await response.json();
          setBlocosDisponiveis(data || []);
        }
      } catch (error) {
        console.error('Erro ao buscar blocos:', error);
      }
    };
    fetchBlocos();
  }, []);

  useEffect(() => {
    if (editingPergunta) {
      // Criar cópias profundas dos arrays para evitar problemas de referência
      const opcoesCopia = editingPergunta.opcoes ? [...editingPergunta.opcoes] : [];
      const regrasNavegacaoCopia = editingPergunta.regras_navegacao 
        ? editingPergunta.regras_navegacao.map(regra => ({ ...regra }))
        : [];
      
      // Verificar se todas as opções estão marcadas para encerrar
      const todasEncerram = editingPergunta.tipo_resposta === 'multipla_escolha' 
        ? todasOpcoesEncerram(opcoesCopia, regrasNavegacaoCopia)
        : false;
      
      // Se todas as opções encerram, marcar como fim automaticamente
      const finalizaFluxo = todasEncerram || editingPergunta.finaliza_fluxo === true;
      
      setPerguntaFormData({
        texto: editingPergunta.texto,
        tipo_resposta: editingPergunta.tipo_resposta,
        opcoes: opcoesCopia,
        ordem: editingPergunta.ordem || 0,
        proxima_pergunta_id: editingPergunta.proxima_pergunta_id,
        regras_navegacao: regrasNavegacaoCopia,
        finaliza_fluxo: finalizaFluxo
      });
      const isDiscursiva = editingPergunta.tipo_resposta === 'discursiva';
      const temRegras = !isDiscursiva && !editingPergunta.finaliza_fluxo && (editingPergunta.regras_navegacao !== undefined || !!editingPergunta.proxima_pergunta_id);
      setTemFluxoCondicional(temRegras);
      const vinculo = obterVinculoAnterior(editingPergunta.id, todasPerguntas);
      if (vinculo) {
        setPerguntaAnteriorId(vinculo.pergunta.id);
        setTipoPerguntaAnterior(vinculo.tipo);
        setRespostasAnteriores(vinculo.respostas);
        
        // No contexto de blocos, sempre usar tipo "pergunta"
        if (isBlocoContext) {
          setTipoVinculo('pergunta');
          setBlocoSelecionadoId('');
        } else {
          // Verificar se há vínculo com bloco nas regras de navegação da pergunta anterior
          // Quando uma pergunta é vinculada através de um bloco, o bloco está nas regras da pergunta anterior
          const regrasComBloco = (vinculo.pergunta.regras_navegacao || []).filter(
            r => r.proximo_bloco_id && vinculo.respostas.includes(r.resposta)
          );
          if (regrasComBloco.length > 0) {
            setTipoVinculo('bloco');
            setBlocoSelecionadoId(regrasComBloco[0].proximo_bloco_id || '');
          } else {
            setTipoVinculo('pergunta');
            setBlocoSelecionadoId('');
          }
        }
      } else {
        setPerguntaAnteriorId('');
        setTipoPerguntaAnterior('');
        setRespostasAnteriores([]);
        setTipoVinculo('pergunta');
        setBlocoSelecionadoId('');
      }
    } else {
      setPerguntaFormData({
        texto: '',
        tipo_resposta: 'discursiva',
        opcoes: [],
        ordem: (todasPerguntas.length || 0) + 1,
        regras_navegacao: [],
        finaliza_fluxo: false
      });
      setTemFluxoCondicional(false);
      setPerguntaAnteriorId('');
      setTipoPerguntaAnterior('');
      setRespostasAnteriores([]);
      setTipoVinculo('pergunta');
      setBlocoSelecionadoId('');
    }
  }, [editingPergunta, todasPerguntas, obterVinculoAnterior, isBlocoContext]);

  const perguntasOrdenadasPorOrdem = useMemo(() => (
    todasPerguntas
      .slice()
      .sort((a, b) => (a.ordem || 0) - (b.ordem || 0))
  ), [todasPerguntas]);

  // Função helper para verificar se todas as opções estão marcadas para encerrar
  const todasOpcoesEncerram = (opcoes: string[], regras: RegraNavegacao[]): boolean => {
    const opcoesValidas = opcoes.map((opcao) => opcao.trim()).filter((opcao) => opcao !== '');
    if (opcoesValidas.length === 0) {
      return false;
    }
    
    // Verificar se todas as opções válidas têm regras que encerram (sem proxima_pergunta_id e sem proximo_bloco_id)
    return opcoesValidas.every((resposta) => {
      const regra = regras.find((r) => r.resposta === resposta);
      return regra && !regra.proxima_pergunta_id && !regra.proximo_bloco_id;
    });
  };

  const atualizarRegrasPergunta = (transformador: (regras: RegraNavegacao[]) => RegraNavegacao[]) => {
    setPerguntaFormData((prev) => {
      const regrasAtuais = prev.regras_navegacao ? [...prev.regras_navegacao] : [];
      const novasRegras = transformador(regrasAtuais).filter((regra) => regra.resposta && regra.resposta.trim() !== '');
      setTemFluxoCondicional(novasRegras.length > 0);
      
      // Verificar se todas as opções estão marcadas para encerrar
      const opcoes = prev.opcoes || [];
      const todasEncerram = todasOpcoesEncerram(opcoes, novasRegras);
      
      // Se todas as opções encerram, marcar a pergunta como fim automaticamente
      // Se foi marcado manualmente como fim (sem regras), manter true
      // Caso contrário, false
      const finalizaFluxo = todasEncerram 
        ? true 
        : (prev.finaliza_fluxo === true && novasRegras.length === 0 ? true : false);
      
      return {
        ...prev,
        regras_navegacao: novasRegras,
        finaliza_fluxo: finalizaFluxo
      };
    });
  };

  const handleAddOpcao = () => {
    const opcoes = perguntaFormData.opcoes || [];
    setPerguntaFormData({
      ...perguntaFormData,
      opcoes: [...opcoes, '']
    });
  };

  const handleUpdateOpcao = (index: number, value: string) => {
    const opcoes = perguntaFormData.opcoes || [];
    const opcaoAnterior = opcoes[index] ?? '';
    const opcaoAnteriorNormalizada = opcaoAnterior.trim();
    const valorNormalizado = value.trim();
    const novasOpcoes = [...opcoes];
    novasOpcoes[index] = value;

    setPerguntaFormData({
      ...perguntaFormData,
      opcoes: novasOpcoes
    });

    atualizarRegrasPergunta((regras) => {
      let atualizadas = [...regras];
      if (opcaoAnteriorNormalizada && opcaoAnteriorNormalizada !== valorNormalizado) {
        atualizadas = atualizadas.map((regra) =>
          regra.resposta === opcaoAnteriorNormalizada
            ? { ...regra, resposta: valorNormalizado }
            : regra
        );
      }
      if (!valorNormalizado && opcaoAnteriorNormalizada) {
        atualizadas = atualizadas.filter((regra) => regra.resposta !== opcaoAnteriorNormalizada);
      }
      return atualizadas;
    });
  };

  const handleRemoveOpcao = (index: number) => {
    const opcoes = perguntaFormData.opcoes || [];
    const opcaoRemovida = opcoes[index]?.trim();
    const novasOpcoes = opcoes.filter((_, i) => i !== index);

    setPerguntaFormData({
      ...perguntaFormData,
      opcoes: novasOpcoes
    });

    if (opcaoRemovida) {
      atualizarRegrasPergunta((regras) =>
        regras.filter((regra) => regra.resposta !== opcaoRemovida)
      );
    }
  };

  const handleToggleFimOpcao = (opcao: string, finalizar: boolean) => {
    const resposta = opcao.trim();
    if (!resposta) {
      return;
    }
    atualizarRegrasPergunta((regras) => {
      const indice = regras.findIndex((regra) => regra.resposta === resposta);
      if (finalizar) {
        if (indice >= 0) {
          if (regras[indice].proxima_pergunta_id) {
            return regras;
          }
          const atualizadas = [...regras];
          atualizadas[indice] = {
            ...atualizadas[indice],
            resposta,
            proxima_pergunta_id: undefined
          };
          return atualizadas;
        }
        return [...regras, { resposta, proxima_pergunta_id: undefined }];
      }
      if (indice >= 0 && !regras[indice].proxima_pergunta_id) {
        const atualizadas = [...regras];
        atualizadas.splice(indice, 1);
        return atualizadas;
      }
      return regras;
    });
  };

  const handleMarkAllOptionsFim = () => {
    const opcoesValidas = (perguntaFormData.opcoes || []).map((opcao) => opcao.trim()).filter((opcao) => opcao !== '');
    if (opcoesValidas.length === 0) {
      return;
    }
    atualizarRegrasPergunta((regras) => {
      const atualizadas = [...regras];
      opcoesValidas.forEach((resposta) => {
        const indice = atualizadas.findIndex((regra) => regra.resposta === resposta);
        if (indice >= 0) {
          if (atualizadas[indice].proxima_pergunta_id) {
            return;
          }
          atualizadas[indice] = {
            ...atualizadas[indice],
            proxima_pergunta_id: undefined
          };
        } else {
          atualizadas.push({ resposta, proxima_pergunta_id: undefined });
        }
      });
      return atualizadas;
    });
  };

  const handleTogglePerguntaFinal = () => {
    const desejaFinalizar = !(perguntaFormData.finaliza_fluxo === true);

    if (desejaFinalizar && editingPergunta) {
      const perguntaIdAtual = editingPergunta.id;
      const possuiDependentes = todasPerguntas.some((pergunta) => {
        if (pergunta.id === perguntaIdAtual) {
          return false;
        }
        if (pergunta.proxima_pergunta_id === perguntaIdAtual) {
          return true;
        }
        const regras = Array.isArray(pergunta.regras_navegacao) ? pergunta.regras_navegacao : [];
        return regras.some((regra) => regra.proxima_pergunta_id === perguntaIdAtual);
      });

      if (possuiDependentes) {
        toast({
          title: "Não é possível marcar como Fim",
          description: "Outras perguntas dependem desta pergunta. Ajuste os vínculos antes de marcar como fim de fluxo.",
          variant: "destructive"
        });
        return;
      }
    }

    if (desejaFinalizar) {
      setTemFluxoCondicional(false);
    }

    setPerguntaFormData((prev) => {
      const finaliza = !(prev.finaliza_fluxo === true);
      if (finaliza) {
        return {
          ...prev,
          finaliza_fluxo: true,
          proxima_pergunta_id: undefined,
          regras_navegacao: []
        };
      }
      return {
        ...prev,
        finaliza_fluxo: false
      };
    });
  };

  const handleSave = () => {
    // No contexto de blocos, sempre usar tipo "pergunta", então não pode ser bloco
    const tipoVinculoFinal = isBlocoContext ? 'pergunta' : tipoVinculo;
    
    // Validar texto da pergunta apenas quando não for tipo de vínculo "bloco"
    if (tipoVinculoFinal !== 'bloco' && (!perguntaFormData.texto || !perguntaFormData.texto.trim())) {
      toast({
        title: "Erro",
        description: "O texto da pergunta é obrigatório",
        variant: "destructive"
      });
      return;
    }

    // Definir regrasAtuais antes de qualquer validação para evitar erro de variável não definida
    const regrasAtuais = perguntaFormData.regras_navegacao || [];

    // Validações de tipo de resposta e opções - apenas quando não for tipo de vínculo "bloco"
    if (tipoVinculoFinal !== 'bloco') {
      if (
        perguntaFormData.tipo_resposta === 'multipla_escolha' &&
        (!perguntaFormData.opcoes || perguntaFormData.opcoes.length === 0)
      ) {
        toast({
          title: "Erro",
          description: "Adicione pelo menos uma opção para múltipla escolha",
          variant: "destructive"
        });
        return;
      }

      if (perguntaFormData.tipo_resposta === 'discursiva' && regrasAtuais.length > 0) {
        toast({
          title: "Erro",
          description:
            "Perguntas discursivas não podem ter fluxo condicional. Apenas perguntas de múltipla escolha podem ter fluxo condicional.",
          variant: "destructive"
        });
        return;
      }
    }

    const opcoesAtuais = (perguntaFormData.opcoes || []).map((opcao) => opcao.trim());
    const opcoesValidas = opcoesAtuais.filter((opcao) => opcao !== '');
    const estaEditando = Boolean(editingPergunta);
    const totalPerguntasAntes = todasPerguntas.length;
    const estaCriandoPrimeiraPergunta = !estaEditando && totalPerguntasAntes === 0;
    const perguntaIdAtual = editingPergunta ? editingPergunta.id : `pergunta-${Date.now()}`;
    const finalizaFluxo = tipoVinculoFinal === 'bloco' ? false : (perguntaFormData.finaliza_fluxo === true);

    const perguntaAnterior = perguntaAnteriorId
      ? todasPerguntas.find((p) => p.id === perguntaAnteriorId)
      : undefined;

    if (perguntaAnteriorId && perguntaAnterior?.finaliza_fluxo) {
      toast({
        title: "Fluxo finalizado",
        description: "A pergunta selecionada encerra o fluxo e não pode ter perguntas subsequentes.",
        variant: "destructive"
      });
      return;
    }

    if (!estaCriandoPrimeiraPergunta && !estaEditando) {
      if (!perguntaAnteriorId) {
        toast({
          title: "Erro",
          description: "Selecione qual pergunta antecede esta pergunta",
          variant: "destructive"
        });
        return;
      }

      if (!perguntaAnterior) {
        toast({
          title: "Erro",
          description: "A pergunta anterior selecionada não foi encontrada",
          variant: "destructive"
        });
        return;
      }

      // Validar se bloco foi selecionado quando tipo de vínculo é bloco (apenas fora do contexto de blocos)
      if (!isBlocoContext && tipoVinculo === 'bloco' && !blocoSelecionadoId) {
        toast({
          title: "Erro",
          description: "Selecione um bloco de perguntas para vincular",
          variant: "destructive"
        });
        return;
      }

      if (perguntaAnterior.tipo_resposta === 'multipla_escolha') {
        if (!perguntaAnterior.opcoes || perguntaAnterior.opcoes.length === 0) {
          toast({
            title: "Erro",
            description: "A pergunta anterior selecionada não possui opções configuradas",
            variant: "destructive"
          });
          return;
        }

        if (respostasAnteriores.length === 0) {
          toast({
            title: "Erro",
            description: "Selecione ao menos uma resposta que direcionará para o vínculo",
            variant: "destructive"
          });
          return;
        }

        const respostasInvalidas = respostasAnteriores.filter(
          (resposta) => !perguntaAnterior.opcoes?.includes(resposta)
        );

        if (respostasInvalidas.length > 0) {
          toast({
            title: "Erro",
            description: `As respostas ${respostasInvalidas.join(', ')} não pertencem à pergunta anterior selecionada`,
            variant: "destructive"
          });
          return;
        }

        const respostasEmUso = new Set<string>();
        (perguntaAnterior.regras_navegacao || []).forEach((regra) => {
          if (regra.resposta) {
            // No contexto de blocos, só verificar proxima_pergunta_id
            if (isBlocoContext) {
              if (regra.proxima_pergunta_id && regra.proxima_pergunta_id !== perguntaIdAtual) {
                respostasEmUso.add(regra.resposta);
              }
            } else {
              // Fora do contexto de blocos, verificar ambos
              // Se estamos vinculando a uma pergunta (não a um bloco), qualquer resposta já vinculada a um bloco deve ser marcada como indisponível
              if (tipoVinculoFinal === 'pergunta') {
                // Se estamos vinculando a uma pergunta, marcar como indisponível qualquer resposta que já esteja vinculada a outro vínculo (pergunta ou bloco)
                if ((regra.proxima_pergunta_id && regra.proxima_pergunta_id !== perguntaIdAtual) ||
                    (regra.proximo_bloco_id)) {
                  respostasEmUso.add(regra.resposta);
                }
              } else {
                // Se estamos vinculando a um bloco, marcar como indisponível respostas vinculadas a outras perguntas ou outros blocos
                if ((regra.proxima_pergunta_id && regra.proxima_pergunta_id !== perguntaIdAtual) ||
                    (regra.proximo_bloco_id && regra.proximo_bloco_id !== blocoSelecionadoId)) {
                  respostasEmUso.add(regra.resposta);
                }
              }
            }
          }
        });

        const respostasConflitantes = respostasAnteriores.filter((resposta) => respostasEmUso.has(resposta));
        if (respostasConflitantes.length > 0) {
          toast({
            title: "Erro",
            description: `As respostas ${respostasConflitantes.join(
              ', '
            )} já direcionam para outro vínculo. Ajuste os vínculos antes de continuar.`,
            variant: "destructive"
          });
          return;
        }
      } else if (perguntaAnterior.tipo_resposta === 'discursiva') {
        // Para perguntas discursivas, verificar se já tem uma sequência definida
        // Mas permitir se o tipo de vínculo for "bloco"
        if (tipoVinculoFinal !== 'bloco' && 
            perguntaAnterior.proxima_pergunta_id &&
            perguntaAnterior.proxima_pergunta_id !== perguntaIdAtual) {
          toast({
            title: "Erro",
            description:
              "A pergunta anterior selecionada já possui uma sequência definida. Ajuste o vínculo existente antes de prosseguir.",
            variant: "destructive"
          });
          return;
        }
      }
    }

    // Limpar regras de navegação com proxima_pergunta_id vazio ou sem resposta
    const regrasLimpas = regrasAtuais
      .map((regra) => ({
        ...regra,
        resposta: (regra.resposta || '').trim(),
        proxima_pergunta_id: regra.proxima_pergunta_id || undefined,
        proximo_bloco_id: regra.proximo_bloco_id || undefined
      }))
      .filter((regra) => regra.resposta && regra.resposta !== '');

    const temRegrasPosLimpeza = regrasLimpas.length > 0;

    const fluxoCondicionalFinal =
      tipoVinculoFinal !== 'bloco' &&
      !finalizaFluxo &&
      perguntaFormData.tipo_resposta === 'multipla_escolha' &&
      temRegrasPosLimpeza;

    const opcoesParaSalvar =
      tipoVinculoFinal === 'bloco' ? undefined : (perguntaFormData.tipo_resposta === 'multipla_escolha' ? opcoesValidas : undefined);

    // Criar regras de navegação baseadas no tipo de vínculo
    let regrasNavegacaoFinal: RegraNavegacao[] | undefined = undefined;
    
    // No contexto de blocos, nunca usar tipo "bloco"
    if (!isBlocoContext && tipoVinculoFinal === 'bloco' && blocoSelecionadoId && !estaCriandoPrimeiraPergunta && perguntaAnteriorId) {
      // Quando é bloco, as regras de navegação serão aplicadas na pergunta anterior
      // Não criamos regras na pergunta atual, apenas marcamos que é um bloco
      // Isso funciona tanto para perguntas de múltipla escolha quanto discursivas
      regrasNavegacaoFinal = undefined;
    } else if (fluxoCondicionalFinal) {
      // Regras de navegação da pergunta atual (quando ela tem múltipla escolha e quer direcionar para outras perguntas)
      // Essas regras são baseadas nas respostas da pergunta atual, não da pergunta anterior
      regrasNavegacaoFinal = regrasLimpas;
    }
    // IMPORTANTE: As regras que vinculam respostas da pergunta anterior à pergunta atual
    // devem ser criadas apenas na pergunta anterior (em TemplateForm.tsx), não na pergunta atual.
    // Não devemos criar regras aqui que apontam para perguntaIdAtual usando respostas da pergunta anterior,
    // pois essas respostas não pertencem à pergunta atual.

    // Quando é bloco (fora do contexto de blocos), não criamos uma pergunta real, apenas um marcador
    // As regras de navegação serão aplicadas na pergunta anterior no TemplateForm
    const perguntaSalva: PerguntaFormulario = {
      id: (!isBlocoContext && tipoVinculoFinal === 'bloco') ? `bloco-${blocoSelecionadoId}-${Date.now()}` : perguntaIdAtual,
      texto: (!isBlocoContext && tipoVinculoFinal === 'bloco') 
        ? `[Bloco: ${blocosDisponiveis.find(b => b.id === blocoSelecionadoId)?.nome || 'Bloco'}]` 
        : (perguntaFormData.texto!.trim()),
      tipo_resposta: (!isBlocoContext && tipoVinculoFinal === 'bloco') ? 'discursiva' : perguntaFormData.tipo_resposta!,
      opcoes: opcoesParaSalvar,
      ordem: perguntaFormData.ordem || (estaEditando ? (editingPergunta?.ordem || 0) : totalPerguntasAntes + 1),
      proxima_pergunta_id: fluxoCondicionalFinal && tipoVinculoFinal === 'pergunta'
        ? perguntaFormData.proxima_pergunta_id || undefined
        : undefined,
      proximo_bloco_id: (!isBlocoContext && tipoVinculoFinal === 'bloco' && blocoSelecionadoId) ? blocoSelecionadoId : undefined,
      regras_navegacao: regrasNavegacaoFinal,
      finaliza_fluxo: finalizaFluxo
    };

    onSave(perguntaSalva, perguntaAnteriorId, respostasAnteriores);
  };

  const isPerguntaFinal = perguntaFormData.finaliza_fluxo === true;
  const podeConfigurarFluxo = perguntaFormData.tipo_resposta === 'multipla_escolha' && !isPerguntaFinal;

  // Lógica para determinar perguntas disponíveis
  const perguntasOrdenadas = todasPerguntas
    .slice()
    .sort((a, b) => (a.ordem || 0) - (b.ordem || 0));
  const idPerguntaAtual = editingPergunta?.id || null;
  const ordemPerguntaAtual = perguntasOrdenadas.find(p => p.id === idPerguntaAtual)?.ordem || 0;

  const respostasJaVinculadas = new Set<string>();
  perguntasOrdenadas.forEach((p) => {
    if (p.id !== idPerguntaAtual) {
      const regras = Array.isArray(p.regras_navegacao) ? p.regras_navegacao : [];
      regras.forEach((regra) => {
        if (regra.resposta) {
          respostasJaVinculadas.add(`${p.id}:${regra.resposta.trim()}`);
        }
      });
    }
  });

  const perguntasDisponiveis = perguntasOrdenadas.filter((p) => {
    if (idPerguntaAtual && p.id === idPerguntaAtual) {
      return false;
    }
    if (p.finaliza_fluxo) {
      return false;
    }
    if (idPerguntaAtual && p.ordem >= ordemPerguntaAtual) {
      return false;
    }
    if (p.tipo_resposta === 'discursiva') {
      // Uma pergunta discursiva está disponível apenas se não tiver proxima_pergunta_id nem proximo_bloco_id
      return !p.proxima_pergunta_id && !p.proximo_bloco_id;
    }
    if (p.tipo_resposta === 'multipla_escolha') {
      const opcoes = p.opcoes || [];
      const regras = Array.isArray(p.regras_navegacao) ? p.regras_navegacao : [];
      return opcoes.some(opcao => {
        const resposta = opcao.trim();
        if (!resposta) return false;
        return !respostasJaVinculadas.has(`${p.id}:${resposta}`);
      });
    }
    return false;
  });

  const perguntaAnteriorSelecionada = todasPerguntas.find(
    (p) => p.id === perguntaAnteriorId
  );
  const respostasJaVinculadasValidacao = new Set<string>();

  if (
    perguntaAnteriorSelecionada?.tipo_resposta === 'multipla_escolha' &&
    perguntaAnteriorSelecionada.regras_navegacao
  ) {
    perguntaAnteriorSelecionada.regras_navegacao.forEach((regra) => {
      if (regra.resposta) {
        // Marcar como indisponível respostas já vinculadas a outras perguntas ou a blocos
        // Se estamos vinculando a uma pergunta, qualquer resposta vinculada a um bloco também deve ser indisponível
        if (tipoVinculo === 'pergunta') {
          // Se estamos vinculando a uma pergunta, marcar como indisponível qualquer resposta já vinculada (pergunta ou bloco)
          if ((regra.proxima_pergunta_id && regra.proxima_pergunta_id !== idPerguntaAtual) ||
              regra.proximo_bloco_id) {
            respostasJaVinculadasValidacao.add(regra.resposta);
          }
        } else {
          // Se estamos vinculando a um bloco, marcar como indisponível respostas vinculadas a outras perguntas ou outros blocos
          if ((regra.proxima_pergunta_id && regra.proxima_pergunta_id !== idPerguntaAtual) ||
              (regra.proximo_bloco_id && regra.proximo_bloco_id !== blocoSelecionadoId)) {
            respostasJaVinculadasValidacao.add(regra.resposta);
          }
        }
      }
    });
  }

  return (
    <Card className="p-4 border-2 border-primary">
      <CardHeader className="p-0 pb-4">
        <CardTitle className="text-base">
          {editingPergunta ? 'Editar Pergunta' : 'Nova Pergunta'}
        </CardTitle>
      </CardHeader>
      <CardContent className="p-0 space-y-4">
        {/* Seção: Vínculo com pergunta anterior */}
        {perguntasDisponiveis.length === 0 ? (
          <div className="border-b pb-4 mb-4">
            <Label className="text-base font-semibold mb-2 block">
              Pergunta anterior
            </Label>
            <p className="text-sm text-muted-foreground">
              Esta será a primeira pergunta do formulário. Ela não possui uma pergunta anterior.
            </p>
          </div>
        ) : (
          <div className="border-b pb-4 mb-4 space-y-3">
            <div>
              <Label className="text-base font-semibold mb-3 block">
                Pergunta anterior *
              </Label>
              <Select
                value={perguntaAnteriorId}
                onValueChange={(value) => {
                  const perguntaSelecionada = todasPerguntas.find(
                    (p) => p.id === value
                  );

                  if (perguntaSelecionada?.finaliza_fluxo) {
                    toast({
                      title: "Fluxo finalizado",
                      description: "Esta pergunta foi marcada como fim de fluxo e não pode ter perguntas subsequentes.",
                      variant: "destructive"
                    });
                    setPerguntaAnteriorId('');
                    setTipoPerguntaAnterior('');
                    setRespostasAnteriores([]);
                    return;
                  }

                  setPerguntaAnteriorId(value);

                  if (perguntaSelecionada) {
                    const tipoAnterior =
                      perguntaSelecionada.tipo_resposta === 'multipla_escolha'
                        ? 'multipla_escolha'
                        : 'discursiva';
                    setTipoPerguntaAnterior(tipoAnterior);

                    if (tipoAnterior === 'multipla_escolha') {
                      const respostasVinculadas = (perguntaSelecionada.regras_navegacao ||
                        [])
                        .filter(
                          (regra) =>
                            regra.proxima_pergunta_id === idPerguntaAtual &&
                            regra.resposta
                        )
                        .map((regra) => regra.resposta as string);
                      setRespostasAnteriores(respostasVinculadas);
                    } else {
                      setRespostasAnteriores([]);
                    }
                  } else {
                    setTipoPerguntaAnterior('');
                    setRespostasAnteriores([]);
                  }
                }}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Selecione a pergunta anterior" />
                </SelectTrigger>
                <SelectContent>
                  {perguntasDisponiveis.map((p) => {
                    const indice = perguntasOrdenadas.findIndex(
                      (item) => item.id === p.id
                    );
                    return (
                      <SelectItem key={p.id} value={p.id}>
                        Pergunta {indice + 1}: {p.texto}
                        {p.finaliza_fluxo ? ' (Fim)' : ''}
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
            </div>

            {perguntaAnteriorSelecionada ? (
              <div className="space-y-3">
                {/* Seleção de respostas - apenas para perguntas de múltipla escolha */}
                {tipoPerguntaAnterior === 'multipla_escolha' && (
                  <div className="space-y-2">
                    <Label>Respostas que direcionam para o vínculo *</Label>
                    <p className="text-xs text-muted-foreground">
                      Selecione uma ou mais respostas da pergunta anterior que devem
                      levar ao vínculo selecionado.
                    </p>
                    <div className="space-y-2">
                      {(perguntaAnteriorSelecionada.opcoes || []).map(
                        (opcao, idx) => {
                          const resposta = opcao.trim();
                          if (!resposta) {
                            return null;
                          }
                          const estaSelecionada = respostasAnteriores.includes(
                            resposta
                          );
                          const respostaReservada = respostasJaVinculadasValidacao.has(
                            resposta
                          );
                          const disabled = respostaReservada && !estaSelecionada;

                          return (
                            <label
                              key={`${perguntaAnteriorSelecionada.id}-resposta-${idx}`}
                              className={`flex items-center gap-2 rounded-md border p-2 text-sm ${
                                disabled ? 'opacity-70 cursor-not-allowed' : ''
                              }`}
                            >
                              <Checkbox
                                checked={estaSelecionada}
                                disabled={disabled}
                                onCheckedChange={(checked) => {
                                  const isChecked = checked === true;
                                  setRespostasAnteriores((prev) => {
                                    if (isChecked) {
                                      if (prev.includes(resposta)) {
                                        return prev;
                                      }
                                      return [...prev, resposta];
                                    }
                                    return prev.filter(
                                      (item) => item !== resposta
                                    );
                                  });
                                }}
                              />
                              <span className="flex-1">{resposta}</span>
                              {disabled && (
                                <span className="text-[11px] text-muted-foreground">
                                  Já vinculada a outra pergunta
                                </span>
                              )}
                            </label>
                          );
                        }
                      )}
                    </div>
                  </div>
                )}

                {/* Mensagem informativa para perguntas discursivas */}
                {tipoPerguntaAnterior === 'discursiva' && (
                  <div className="rounded-md border bg-muted/30 p-3 text-sm text-muted-foreground">
                    Esta pergunta será exibida após{" "}
                    <span className="font-medium text-foreground">
                      {getPerguntaLabel(perguntaAnteriorSelecionada.id, todasPerguntas)}
                    </span>{" "}
                    independentemente da resposta fornecida.
                  </div>
                )}
                
                {/* Seleção do tipo de vínculo - apenas se não for contexto de bloco */}
                {!isBlocoContext && (
                  <>
                    <div className="space-y-2">
                      <Label>Tipo de vínculo *</Label>
                      <Select
                        value={tipoVinculo}
                        onValueChange={(value: 'pergunta' | 'bloco') => {
                          setTipoVinculo(value);
                          if (value === 'pergunta') {
                            setBlocoSelecionadoId('');
                          } else {
                            // Se mudar para bloco, limpar proxima_pergunta_id das regras
                            setPerguntaFormData(prev => ({
                              ...prev,
                              proxima_pergunta_id: undefined
                            }));
                          }
                        }}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="pergunta">Pergunta</SelectItem>
                          <SelectItem value="bloco">Bloco de Perguntas</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    {/* Seleção do bloco quando tipo de vínculo é bloco */}
                    {tipoVinculo === 'bloco' && (
                      <div className="space-y-2">
                        <Label>Bloco de Perguntas *</Label>
                        <Select
                          value={blocoSelecionadoId}
                          onValueChange={(value) => {
                            setBlocoSelecionadoId(value);
                          }}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Selecione um bloco" />
                          </SelectTrigger>
                          <SelectContent>
                            {blocosDisponiveis.length === 0 ? (
                              <SelectItem value="" disabled>
                                Nenhum bloco disponível
                              </SelectItem>
                            ) : (
                              blocosDisponiveis.map((bloco) => (
                                <SelectItem key={bloco.id} value={bloco.id}>
                                  {bloco.nome} ({bloco.perguntas?.length || 0} pergunta{bloco.perguntas?.length !== 1 ? 's' : ''})
                                </SelectItem>
                              ))
                            )}
                          </SelectContent>
                        </Select>
                        {blocoSelecionadoId && (
                          <p className="text-xs text-muted-foreground">
                            {tipoPerguntaAnterior === 'multipla_escolha' 
                              ? 'O bloco selecionado será inserido após as respostas escolhidas acima.'
                              : 'O bloco selecionado será inserido após a pergunta anterior.'}
                          </p>
                        )}
                      </div>
                    )}
                  </>
                )}
              </div>
            ) : null}
          </div>
        )}

        {/* Campos específicos de pergunta - só aparecem quando tipo de vínculo é "pergunta" ou quando não há seleção de tipo de vínculo */}
        {(!isBlocoContext && tipoVinculo === 'bloco') ? null : (
          <>
            <div>
              <Label htmlFor="pergunta-texto">Texto da Pergunta *</Label>
              <Textarea
                id="pergunta-texto"
                value={perguntaFormData.texto}
                onChange={(e) => setPerguntaFormData({ ...perguntaFormData, texto: e.target.value })}
                placeholder="Ex: Você mora em casa ou apartamento?"
                rows={2}
              />
            </div>

            <div>
              <Label htmlFor="pergunta-tipo">Tipo de Resposta *</Label>
              <Select
                value={perguntaFormData.tipo_resposta}
                onValueChange={(value: 'multipla_escolha' | 'discursiva') => {
                  const isDiscursiva = value === 'discursiva';
                  setPerguntaFormData({ 
                    ...perguntaFormData, 
                    tipo_resposta: value,
                    opcoes: value === 'multipla_escolha' ? (perguntaFormData.opcoes || ['']) : undefined
                  });
                  if (isDiscursiva && temFluxoCondicional) {
                    setTemFluxoCondicional(false);
                    setPerguntaFormData(prev => ({
                      ...prev,
                      proxima_pergunta_id: undefined,
                      regras_navegacao: []
                    }));
                  }
                }}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="discursiva">Discursiva (texto livre)</SelectItem>
                  <SelectItem value="multipla_escolha">Múltipla Escolha</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {perguntaFormData.tipo_resposta === 'multipla_escolha' && (
              <div>
                <Label>Opções de Resposta *</Label>
                <div className="space-y-2 mt-2">
                  {(perguntaFormData.opcoes || []).map((opcao, index) => (
                    <div key={index} className="flex gap-2">
                      <Input
                        value={opcao}
                        onChange={(e) => handleUpdateOpcao(index, e.target.value)}
                        placeholder={`Opção ${index + 1}`}
                      />
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => handleRemoveOpcao(index)}
                        className="text-red-600"
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={handleAddOpcao}
                    className="w-full"
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    Adicionar Opção
                  </Button>
                </div>
                {/* Seção de Encaminhamentos - mostra apenas os vínculos das alternativas desta pergunta */}
                {Array.isArray(perguntaFormData.regras_navegacao) && perguntaFormData.regras_navegacao.length > 0 && (
                  <div className="mt-4 border-t pt-4">
                    <Label className="text-sm font-semibold mb-2 block">Encaminhamentos:</Label>
                    <ul className="text-xs text-muted-foreground space-y-1">
                      {perguntaFormData.regras_navegacao
                        .filter(regra => {
                          // Filtrar apenas regras cujas respostas correspondem às opções desta pergunta
                          const resposta = regra.resposta?.trim();
                          if (!resposta) return false;
                          return (perguntaFormData.opcoes || []).some(opcao => opcao.trim() === resposta);
                        })
                        .map((regra, regraIdx) => {
                          const blocoVinculado = regra.proximo_bloco_id 
                            ? blocosDisponiveis.find(b => b.id === regra.proximo_bloco_id)
                            : null;
                          return (
                            <li key={`encaminhamento-${regraIdx}`}>
                              <span className="font-semibold text-foreground">{regra.resposta || `Opção ${regraIdx + 1}`}</span>
                              <span className="mx-1 text-muted-foreground">→</span>
                              <span>
                                {regra.proxima_pergunta_id
                                  ? getPerguntaLabel(regra.proxima_pergunta_id, todasPerguntas)
                                  : regra.proximo_bloco_id && blocoVinculado
                                  ? `${blocoVinculado.nome} (bloco)`
                                  : 'Encerrar formulário'}
                              </span>
                            </li>
                          );
                        })}
                    </ul>
                  </div>
                )}
              </div>
            )}

            <div className="rounded-md border bg-muted/30 p-3 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <p className="text-sm font-medium text-foreground">Fim do fluxo</p>
                <p className="text-xs text-muted-foreground">
                  {isBlocoContext 
                    ? "Marque quando esta pergunta deve encerrar o bloco. Perguntas finais não podem direcionar para outras perguntas."
                    : "Marque quando esta pergunta deve encerrar o fluxo. Perguntas finais não podem direcionar para outras perguntas."}
                </p>
              </div>
              <Button
                type="button"
                variant={isPerguntaFinal ? "default" : "outline"}
                size="sm"
                onClick={handleTogglePerguntaFinal}
              >
                {isPerguntaFinal ? 'Remover Fim' : 'Marcar como Fim'}
              </Button>
            </div>

            {podeConfigurarFluxo && (
              <div className="space-y-3">
                <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                  <Label className="text-sm font-semibold">Encerrar por opção</Label>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={handleMarkAllOptionsFim}
                    disabled={
                      !((perguntaFormData.opcoes || []).some((opcao) => {
                        const resposta = opcao.trim();
                        if (!resposta) return false;
                        const regra = (perguntaFormData.regras_navegacao || []).find(
                          (r) => r.resposta === resposta
                        );
                        return !(regra && regra.proxima_pergunta_id);
                      }))
                    }
                  >
                    Marcar todas como fim
                  </Button>
                </div>
                <div className="space-y-2">
                  {(perguntaFormData.opcoes || []).map((opcao, idx) => {
                    const resposta = opcao.trim();
                    const regra = (perguntaFormData.regras_navegacao || []).find(
                      (r) => r.resposta === resposta
                    );
                    const possuiDestinoPergunta = Boolean(regra?.proxima_pergunta_id);
                    const possuiDestinoBloco = Boolean(regra?.proximo_bloco_id);
                    const possuiDestino = possuiDestinoPergunta || possuiDestinoBloco;
                    const encerraFluxo = Boolean(regra) && !possuiDestino;
                    const destinoLabel = regra?.proxima_pergunta_id
                      ? getPerguntaLabel(
                          regra.proxima_pergunta_id,
                          todasPerguntas
                        )
                      : regra?.proximo_bloco_id
                      ? blocosDisponiveis.find(b => b.id === regra.proximo_bloco_id)?.nome || 'Bloco'
                      : null;

                    return (
                      <div key={`${opcao}-${idx}`} className="space-y-2 rounded-md border p-3">
                        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                          <span className="font-medium">
                            {opcao || `Opção ${idx + 1}`}
                          </span>
                          <label className={`flex items-center gap-2 ${possuiDestino ? 'text-muted-foreground' : ''}`}>
                            <Checkbox
                              checked={encerraFluxo}
                              disabled={!resposta || possuiDestino}
                              onCheckedChange={(checked) =>
                                handleToggleFimOpcao(opcao, checked === true)
                              }
                            />
                            <span className="text-sm">{isBlocoContext ? 'Encerrar bloco' : 'Encerrar formulário'}</span>
                          </label>
                        </div>
                        {possuiDestino && destinoLabel && (
                          <p className="text-xs text-muted-foreground">
                            Esta resposta direciona para {destinoLabel} {possuiDestinoBloco ? '(bloco)' : ''}. Ajuste o vínculo para encerrar {isBlocoContext ? 'o bloco' : 'o fluxo'} por aqui.
                          </p>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </>
        )}

        <div className="flex justify-end gap-2 pt-2 border-t">
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={onCancel}
          >
            <X className="h-4 w-4 mr-2" />
            Cancelar
          </Button>
          <Button
            type="button"
            size="sm"
            onClick={handleSave}
          >
            <Save className="h-4 w-4 mr-2" />
            {editingPergunta ? 'Atualizar' : 'Adicionar'} Pergunta
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}


